/**
 * RTK Query API для массовых операций и визуализации сеток
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQueryWithReauth';
import type {
  PlotGridView,
  FieldGridView,
  BulkEventRequest,
  BulkEventResponse,
  BulkBedsWithPlantingsRequest,
  BulkBedsWithPlantingsResponse,
} from '../types/bulkOperations';
import { loadSaveWorldSnapshot, toQueryFnError } from './saveWorldQuery';

export const bulkOperationsApi = createApi({
  reducerPath: 'bulkOperationsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['GridView', 'BulkEvents'],
  endpoints: (builder) => ({
    // ============ Bulk Beds with Plantings ============
    
    createBulkBedsWithPlantings: builder.mutation<
      BulkBedsWithPlantingsResponse, 
      { saveId: string; plotId: string; fieldId?: string; data: BulkBedsWithPlantingsRequest }
    >({
      query: ({ saveId, plotId, fieldId, data }) => ({
        url: `/saves/${saveId}/plots/${plotId}/bulk-beds-with-plantings`,
        method: 'POST',
        params: fieldId ? { fieldId } : undefined,
        body: data,
      }),
      invalidatesTags: (result, error, { plotId }) => [
        { type: 'GridView', id: `plot-${plotId}` },
      ],
    }),

    // ============ Grid Visualization ============

    getPlotGridView: builder.query<
      PlotGridView,
      {
        plotId: string;
        saveId: string;
        colorMode?: 'crop' | 'status' | 'empty' | 'health';
        includeEmpty?: boolean;
        layers?: Array<'watering' | 'issues'>;
        layerWindowDays?: number;
        problemEventTypes?: string[];
        wateringEventTypes?: string[];
      }
    >({
      queryFn: async ({ saveId, plotId, colorMode }, api) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId);
          const beds = world.structure.beds.filter((bed) => String(bed.plotId) === String(plotId));
          const plantingsByBed = new Map<string, typeof world.structure.plantings>();
          world.structure.plantings.forEach((planting) => {
            if (!planting.bedId) return;
            const key = String(planting.bedId);
            const list = plantingsByBed.get(key) || [];
            list.push(planting);
            plantingsByBed.set(key, list);
          });
          const cells = beds.flatMap((bed) => bed.cells.map((cell) => ({
            row: Number(cell.row) + 1,
            position: Number(cell.col) + 1,
            bedId: bed.id,
            bedName: bed.name,
            plantings: (plantingsByBed.get(String(bed.id)) || []).map((planting) => ({
              id: planting.id,
              cropId: planting.cropId || planting.id,
              cropName: planting.crop || 'Культура',
              cropLatinName: '',
              lifecycleStatus: (() => {
                const structuralStatus = String(planting.lifecycleStatus || 'active').toLowerCase();
                const runtime = world.runtime?.plantings?.[String(planting.id)];
                const runtimeStatus = String(runtime?.lifecycleStatus || '').toLowerCase();
                const vegetationStage = String(runtime?.vegetationStage || '').toLowerCase();
                const lifecyclePhase = String(runtime?.lifecyclePhase || '').toLowerCase();
                if (['archived', 'sold', 'transferred', 'holding'].includes(structuralStatus)) {
                  return structuralStatus;
                }
                if (runtime?.isDead || runtimeStatus === 'dead' || vegetationStage === 'dead' || lifecyclePhase === 'dead') {
                  return 'dead';
                }
                return structuralStatus;
              })() as any,
            })),
          })));
          const rows = cells.map((cell) => cell.row);
          const cols = cells.map((cell) => cell.position);
          return {
            data: {
              plotId,
              colorMode: (colorMode === 'health' ? 'status' : colorMode) || 'crop',
              gridDimensions: {
                rows: rows.length ? Math.max(...rows) : 0,
                columns: cols.length ? Math.max(...cols) : 0,
                totalCells: cells.length,
                occupiedCells: cells.filter((cell) => cell.plantings.length > 0).length,
              },
              cells,
              layers: { watering: { byBed: {} }, issues: { byBed: {} } },
            },
          };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { plotId }) => [
        { type: 'GridView', id: `plot-${plotId}` },
      ],
    }),
    
    getFieldGridView: builder.query<FieldGridView, { saveId: string; fieldId: string }>({
      queryFn: async ({ saveId, fieldId }, api) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId);
          return {
            data: {
              fieldId,
              viewMode: 'field',
              plots: world.structure.plots
                .filter((plot) => String(plot.fieldId) === String(fieldId))
                .map((plot) => {
                  const beds = world.structure.beds.filter((bed) => String(bed.plotId) === String(plot.id));
                  return {
                    plotId: plot.id,
                    plotName: plot.name || plot.id,
                    area: Number(plot.area || 0),
                    totalBeds: beds.length,
                    occupiedBeds: beds.filter((bed) => world.structure.plantings.some((planting) => String(planting.bedId) === String(bed.id))).length,
                    uniqueCrops: new Set(world.structure.plantings.filter((planting) => beds.some((bed) => String(bed.id) === String(planting.bedId))).map((planting) => planting.cropId || planting.crop)).size,
                    occupancyRate: beds.length ? Math.round((beds.filter((bed) => world.structure.plantings.some((planting) => String(planting.bedId) === String(bed.id))).length / beds.length) * 100) : 0,
                  };
                }),
            },
          };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { fieldId }) => [
        { type: 'GridView', id: `field-${fieldId}` },
      ],
    }),

    // ============ Bulk Events (save-scoped, Wave 5.2) ============

    createBulkEvents: builder.mutation<
      BulkEventResponse,
      BulkEventRequest & { saveId: string }
    >({
      query: ({ saveId, ...data }) => ({
        url: `/saves/${saveId}/bulk-events`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, { saveId }) => [
        'BulkEvents',
        // Bulk events may include retro-dated entries that trigger session-wide
        // replay; refresh world snapshot so current_attributes reflect new state.
        { type: 'SaveV2' as any, id: `world-${saveId}` },
      ],
    }),
  }),
});

export const {
  useCreateBulkBedsWithPlantingsMutation,
  useGetPlotGridViewQuery,
  useGetFieldGridViewQuery,
  useCreateBulkEventsMutation,
} = bulkOperationsApi;
